import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ChevronRight,
  Check,
  FileText,
  Users,
  MapPin,
  Leaf,
  ArrowRight,
  Shield,
  Factory,
  Truck,
} from "lucide-react";

export default function SustainabilityPage() {
  const processSteps = [
    {
      icon: Factory,
      name: "Shearing",
      description: "Sheep are sheared once or twice yearly by skilled shearers. This is a necessary welfare procedure—wool grows continuously and must be removed.",
      location: "Partner farms across the Netherlands",
      why: "We collect wool during the regular shearing schedule, not disrupting farm operations."
    },
    {
      icon: Users,
      name: "Sorting",
      description: "Each fleece is hand-sorted by grade, color, and fiber length. We specifically select wool that would otherwise be discarded.",
      location: "WoolSort BV (Den Helder, NL) or Wol Collectief Friesland (Leeuwarden, NL)",
      why: "Expert sorting ensures only suitable fibers enter our products while maximizing waste recovery."
    },
    {
      icon: Factory,
      name: "Cleaning",
      description: "Raw wool is washed to remove lanolin, dirt, and vegetable matter. We use closed-loop water systems where possible.",
      location: "Wollwäscherei Schmidt (Bremen, DE) or Texelse Wol (Texel, NL)",
      why: "Local cleaning facilities reduce transport emissions and support regional industry."
    },
    {
      icon: Factory,
      name: "Spinning",
      description: "Clean wool is carded (aligned) and spun into yarn. Different products require different yarn weights and twist patterns.",
      location: "Spinnerij Van der Berg (Tilburg, NL) or Wolspinnerij Veendam (Veendam, NL)",
      why: "Dutch spinning mills preserve centuries of textile knowledge while meeting modern quality standards."
    },
    {
      icon: Factory,
      name: "Knitting/Weaving",
      description: "Yarn is transformed into fabric through knitting (for sweaters, accessories) or weaving (for blankets, home textiles).",
      location: "Breifabriek Leiden (Leiden, NL) or Weverij Overijssel (Almelo, NL)",
      why: "Small-batch production allows us to maintain quality control and respond to actual demand."
    },
    {
      icon: Truck,
      name: "Finishing",
      description: "Final quality inspection, washing, pressing, and labeling. Each product receives its traceability documentation.",
      location: "WoolStory Atelier (Amsterdam, NL)",
      why: "In-house finishing ensures every product meets our standards before reaching you."
    }
  ];

  const whyLocal = [
    {
      title: "Reduced Transport Emissions",
      description: "Processing within NL/DE means wool never travels more than 300km between steps, compared to 10,000+ km for typical global supply chains."
    },
    {
      title: "Preserved Craftsmanship",
      description: "Supporting local mills and artisans keeps traditional textile skills alive in the Netherlands and Germany."
    },
    {
      title: "Traceable Chain of Custody",
      description: "Proximity allows us to personally verify each partner's practices and document every transfer."
    },
    {
      title: "Economic Return to Farmers",
      description: "Fair compensation goes directly to Dutch farmers, who previously received nothing or had to pay for wool disposal."
    }
  ];

  const proofLibrary = [
    {
      category: "Partner Documentation",
      items: [
        "Farm partnership agreements with traceability clauses",
        "Mill certifications and capability statements",
        "Quality control protocols for each facility"
      ]
    },
    {
      category: "Process Verification",
      items: [
        "Batch tracking records from farm to finished product",
        "Weight reconciliation reports (wool in vs. product out)",
        "Third-party quality testing results"
      ]
    },
    {
      category: "Impact Measurement",
      items: [
        "Waste wool recovery calculations methodology",
        "Transport distance comparisons with conventional supply chains",
        "Farmer payment records showing above-market rates"
      ]
    }
  ];

  return (
    <div>
      {/* Hero */}
      <section className="bg-secondary/30 py-16 lg:py-24 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-foreground">Sustainability & Transparency</span>
          </nav>

          <div className="max-w-3xl">
            <Badge variant="secondary" className="mb-4">
              <Shield className="h-3 w-3 mr-1" />
              Verified Process
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold text-foreground">
              Sustainability & Transparency
            </h1>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              We believe sustainability claims require proof. This page explains exactly how we transform 
              discarded Dutch wool into products, why each step matters, and where you can verify our claims.
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground">
              Our Process, Step by Step
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Every WoolStory product follows this journey. Each step is documented for full traceability.
            </p>
          </div>

          <div className="space-y-6">
            {processSteps.map((step, index) => (
              <Card key={index} className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="md:flex">
                    {/* Step Number & Icon */}
                    <div className="md:w-48 shrink-0 bg-secondary/50 p-6 flex md:flex-col items-center md:items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <step.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Step {index + 1}
                        </span>
                        <h3 className="font-display text-xl font-semibold text-foreground">
                          {step.name}
                        </h3>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 flex-1">
                      <p className="text-muted-foreground leading-relaxed">
                        {step.description}
                      </p>
                      <div className="mt-4 space-y-2">
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground mt-1 shrink-0" />
                          <span className="text-sm text-muted-foreground">
                            <span className="font-medium text-foreground">Where:</span> {step.location}
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <Leaf className="h-4 w-4 text-success mt-1 shrink-0" />
                          <span className="text-sm text-muted-foreground">
                            <span className="font-medium text-foreground">Why it matters:</span> {step.why}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Local Production */}
      <section className="py-16 lg:py-24 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground">
              Why Local NL/DE Production Matters
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              We could process wool cheaper overseas. Here's why we don't.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {whyLocal.map((reason, index) => (
              <Card key={index}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center shrink-0">
                      <Check className="h-4 w-4 text-success" />
                    </div>
                    <div>
                      <h3 className="font-display text-lg font-semibold text-foreground">
                        {reason.title}
                      </h3>
                      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                        {reason.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Proof Library */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground">
              Proof Library
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Documentation and verification available for our claims. Some documents are available 
              upon request to protect partner confidentiality while maintaining transparency.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {proofLibrary.map((section, index) => (
              <Card key={index} className="bg-secondary/30">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="h-5 w-5 text-primary" />
                    <h3 className="font-display text-lg font-semibold text-foreground">
                      {section.category}
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="text-primary mt-1">•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-muted-foreground mb-4">
              Want to see specific documentation? We're happy to provide verification.
            </p>
            <Button variant="outline" className="gap-2">
              Request Documentation
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Impact Methodology */}
      <section className="py-16 lg:py-24 bg-secondary/20 border-t border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground mb-6">
            How We Calculate Impact
          </h2>
          <div className="prose prose-neutral max-w-none">
            <p className="text-muted-foreground leading-relaxed mb-4">
              Each WoolStory product shows an "Impact" metric—the amount of wool saved from waste. 
              Here's how we calculate it:
            </p>
            <Card className="mb-6">
              <CardContent className="p-6">
                <h4 className="font-display text-lg font-semibold text-foreground mb-3">Calculation Method</h4>
                <ol className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-foreground">1.</span>
                    We weigh the raw wool input for each product batch from our sorting partners.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-foreground">2.</span>
                    This wool is specifically sourced from "waste" grades that farmers couldn't sell commercially.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-foreground">3.</span>
                    We divide the batch wool weight by the number of finished products to get per-item impact.
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-medium text-foreground">4.</span>
                    Processing losses (cleaning, carding) are factored in—we report wool saved, not final product weight.
                  </li>
                </ol>
              </CardContent>
            </Card>
            <p className="text-muted-foreground leading-relaxed">
              These figures represent real wool that would have been burned, buried, or exported as bulk waste. 
              By creating demand for this fiber, we give farmers a reason to collect and sort it rather than dispose of it.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-foreground">
            See Traceability in Action
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Every product includes full traceability information. Explore a product to see 
            exactly where its wool came from and how it was made.
          </p>
          <Button asChild className="mt-8 gap-2">
            <Link to="/products">
              Explore Products
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
